import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Save, Loader2, Plus, Trash2, ImageIcon, Video, Palette } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";

const LANGS = [
    { code: "tr", name: "Türkçe" },
    { code: "en", name: "English" },
    { code: "de", name: "Deutsch" },
];

const BG_TYPES = [
    { value: "gradient", label: "Gradient (varsayılan)", icon: Palette },
    { value: "image", label: "Görsel", icon: ImageIcon },
    { value: "video", label: "Video", icon: Video },
];

const TREND_OPTS = [
    { value: "up", label: "↑ Yükseliş" },
    { value: "down", label: "↓ Düşüş" },
    { value: "stable", label: "→ Sabit" },
];

const emptyTrans = () => ({ tr: "", en: "", de: "" });

export default function AdminHomepage() {
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/homepage-settings");
                // Ensure structure
                if (!data.hero) data.hero = {};
                if (!Array.isArray(data.hero.stats)) data.hero.stats = [];
                setData(data);
            } catch (e) {
                toast.error(formatApiError(e));
            }
        })();
    }, []);

    if (!data) return <div className="text-sm text-slate-500">Yükleniyor...</div>;

    const updateHero = (key, value) =>
        setData((p) => ({ ...p, hero: { ...p.hero, [key]: value } }));

    const updateHeroTrans = (key, lang, value) =>
        setData((p) => ({
            ...p,
            hero: {
                ...p.hero,
                [key]: { ...(p.hero?.[key] || emptyTrans()), [lang]: value },
            },
        }));

    const addStat = () =>
        setData((p) => ({
            ...p,
            hero: {
                ...p.hero,
                stats: [
                    ...(p.hero?.stats || []),
                    { label: emptyTrans(), value: 0, suffix: "", prefix: "", decimals: 0, trend: "stable", trend_pct: 0 },
                ],
            },
        }));

    const updateStat = (idx, key, value) =>
        setData((p) => {
            const stats = [...(p.hero?.stats || [])];
            stats[idx] = { ...stats[idx], [key]: value };
            return { ...p, hero: { ...p.hero, stats } };
        });

    const updateStatLabel = (idx, lang, value) =>
        setData((p) => {
            const stats = [...(p.hero?.stats || [])];
            stats[idx] = {
                ...stats[idx],
                label: { ...(stats[idx].label || emptyTrans()), [lang]: value },
            };
            return { ...p, hero: { ...p.hero, stats } };
        });

    const removeStat = (idx) =>
        setData((p) => {
            const stats = (p.hero?.stats || []).filter((_, i) => i !== idx);
            return { ...p, hero: { ...p.hero, stats } };
        });

    const handleSave = async () => {
        setSaving(true);
        try {
            const { data: saved } = await api.put("/admin/homepage-settings", data);
            setData(saved);
            toast.success("Anasayfa ayarları kaydedildi");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-homepage">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-dark-navy">Anasayfa Yönetimi</h1>
                    <p className="text-sm text-deep-blue/70">Hero, sayaçlar, arka plan, çoklu dil içerikleri.</p>
                </div>
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-dark-navy hover:bg-deep-blue text-white text-sm font-semibold px-4 py-2 rounded-md inline-flex items-center gap-2 disabled:opacity-70"
                    data-testid="homepage-save-btn"
                >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Kaydet
                </button>
            </div>

            {/* HERO METİNLERİ */}
            <Section title="Hero — Çoklu Dil Metinler">
                <Tabs defaultValue="tr" className="w-full">
                    <TabsList className="bg-light-bg">
                        {LANGS.map((l) => (
                            <TabsTrigger
                                key={l.code}
                                value={l.code}
                                data-testid={`homepage-tab-${l.code}`}
                                className="data-[state=active]:bg-dark-navy data-[state=active]:text-white"
                            >
                                {l.name}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                    {LANGS.map((l) => (
                        <TabsContent key={l.code} value={l.code} className="space-y-4 pt-4">
                            <Field
                                label="Eyebrow (Üst pill metni)"
                                value={data.hero.eyebrow?.[l.code] || ""}
                                onChange={(v) => updateHeroTrans("eyebrow", l.code, v)}
                                testId={`homepage-eyebrow-${l.code}`}
                            />
                            <Field
                                label="Başlık (H1)"
                                value={data.hero.title?.[l.code] || ""}
                                onChange={(v) => updateHeroTrans("title", l.code, v)}
                                testId={`homepage-title-${l.code}`}
                            />
                            <Field
                                label="Alt başlık"
                                value={data.hero.subtitle?.[l.code] || ""}
                                onChange={(v) => updateHeroTrans("subtitle", l.code, v)}
                                multiline
                                testId={`homepage-subtitle-${l.code}`}
                            />
                            <div className="grid md:grid-cols-2 gap-4">
                                <Field
                                    label="CTA 1 (Gold)"
                                    value={data.hero.cta1?.[l.code] || ""}
                                    onChange={(v) => updateHeroTrans("cta1", l.code, v)}
                                    testId={`homepage-cta1-${l.code}`}
                                />
                                <Field
                                    label="CTA 2 (Outline)"
                                    value={data.hero.cta2?.[l.code] || ""}
                                    onChange={(v) => updateHeroTrans("cta2", l.code, v)}
                                    testId={`homepage-cta2-${l.code}`}
                                />
                            </div>
                        </TabsContent>
                    ))}
                </Tabs>
            </Section>

            {/* ARKA PLAN */}
            <Section title="Hero — Arka Plan">
                <div>
                    <span className="text-xs font-semibold text-deep-blue/80 uppercase tracking-wider">
                        Arka plan tipi
                    </span>
                    <div className="mt-2 grid grid-cols-3 gap-3">
                        {BG_TYPES.map((b) => {
                            const Icon = b.icon;
                            const active = data.hero.background_type === b.value;
                            return (
                                <button
                                    key={b.value}
                                    type="button"
                                    onClick={() => updateHero("background_type", b.value)}
                                    data-testid={`homepage-bg-${b.value}`}
                                    className={`flex items-center gap-2 px-4 py-3 rounded-md border text-sm font-semibold ${
                                        active
                                            ? "border-teal bg-teal/5 text-teal-700"
                                            : "border-slate-300 bg-white text-dark-navy hover:bg-light-bg"
                                    }`}
                                >
                                    <Icon className="w-4 h-4" />
                                    {b.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
                <Field
                    label="Görsel URL"
                    value={data.hero.background_image_url || ""}
                    onChange={(v) => updateHero("background_image_url", v)}
                    testId="homepage-bg-image-url"
                />
                <Field
                    label="Video URL (mp4 önerilir)"
                    value={data.hero.background_video_url || ""}
                    onChange={(v) => updateHero("background_video_url", v)}
                    testId="homepage-bg-video-url"
                />
            </Section>

            {/* SAYAÇLAR */}
            <Section
                title="Hero — Animasyonlu Sayaçlar"
                action={
                    <button
                        onClick={addStat}
                        className="text-sm font-semibold text-teal-700 hover:text-teal inline-flex items-center gap-1"
                        data-testid="homepage-add-stat"
                    >
                        <Plus className="w-4 h-4" /> Yeni sayaç
                    </button>
                }
            >
                <div className="space-y-4" data-testid="homepage-stats-list">
                    {(data.hero.stats || []).map((stat, idx) => (
                        <div
                            key={idx}
                            className="rounded-md border border-slate-200 bg-light-bg/40 p-4 space-y-3"
                            data-testid={`homepage-stat-${idx}`}
                        >
                            <div className="flex items-center justify-between">
                                <span className="text-xs uppercase tracking-wider text-deep-blue font-semibold">
                                    Sayaç #{idx + 1}
                                </span>
                                <button
                                    onClick={() => removeStat(idx)}
                                    className="text-data-red hover:underline inline-flex items-center gap-1 text-xs"
                                    data-testid={`homepage-stat-remove-${idx}`}
                                >
                                    <Trash2 className="w-3.5 h-3.5" /> Sil
                                </button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                {LANGS.map((l) => (
                                    <Field
                                        key={l.code}
                                        label={`Etiket (${l.code.toUpperCase()})`}
                                        value={stat.label?.[l.code] || ""}
                                        onChange={(v) => updateStatLabel(idx, l.code, v)}
                                    />
                                ))}
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                                <Field
                                    label="Prefix"
                                    value={stat.prefix || ""}
                                    onChange={(v) => updateStat(idx, "prefix", v)}
                                />
                                <NumberField
                                    label="Değer"
                                    value={stat.value}
                                    onChange={(v) => updateStat(idx, "value", v)}
                                />
                                <Field
                                    label="Suffix"
                                    value={stat.suffix || ""}
                                    onChange={(v) => updateStat(idx, "suffix", v)}
                                />
                                <NumberField
                                    label="Ondalık"
                                    value={stat.decimals || 0}
                                    onChange={(v) => updateStat(idx, "decimals", v)}
                                />
                                <SelectField
                                    label="Trend"
                                    value={stat.trend || "stable"}
                                    onChange={(v) => updateStat(idx, "trend", v)}
                                    options={TREND_OPTS}
                                />
                                <NumberField
                                    label="Trend %"
                                    value={stat.trend_pct || 0}
                                    onChange={(v) => updateStat(idx, "trend_pct", v)}
                                />
                            </div>
                        </div>
                    ))}
                    {(data.hero.stats || []).length === 0 && (
                        <div className="text-sm text-slate-500 text-center py-6">
                            Sayaç yok. "Yeni sayaç" ile ekleyin.
                        </div>
                    )}
                </div>
            </Section>
        </div>
    );
}

function Section({ title, children, action }) {
    return (
        <section className="bg-white rounded-lg border border-slate-100 shadow-md p-5 space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold uppercase tracking-wider text-deep-blue">{title}</h2>
                {action}
            </div>
            {children}
        </section>
    );
}

function Field({ label, value, onChange, multiline, testId }) {
    return (
        <label className="block">
            <span className="text-xs font-semibold text-deep-blue/80 uppercase tracking-wider">{label}</span>
            {multiline ? (
                <textarea
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    rows={3}
                    className="mt-1 w-full rounded-md border border-slate-300 focus:border-teal px-3 py-2 text-sm outline-none"
                    data-testid={testId}
                />
            ) : (
                <input
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    className="mt-1 w-full rounded-md border border-slate-300 focus:border-teal px-3 py-2 text-sm outline-none"
                    data-testid={testId}
                />
            )}
        </label>
    );
}

function NumberField({ label, value, onChange }) {
    return (
        <label className="block">
            <span className="text-xs font-semibold text-deep-blue/80 uppercase tracking-wider">{label}</span>
            <input
                type="number"
                step="0.01"
                value={value ?? 0}
                onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
                className="mt-1 w-full rounded-md border border-slate-300 focus:border-teal px-3 py-2 text-sm outline-none font-data"
            />
        </label>
    );
}

function SelectField({ label, value, onChange, options }) {
    return (
        <label className="block">
            <span className="text-xs font-semibold text-deep-blue/80 uppercase tracking-wider">{label}</span>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 focus:border-teal px-3 py-2 text-sm outline-none bg-white"
            >
                {options.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                ))}
            </select>
        </label>
    );
}
